import React, { useState } from 'react';
import { useSpring } from '@react-spring/web';
import NextImage, { StaticImageData } from 'next/image';

import {
    Container,
    ImageContainer,
    ImageLink,
    ImageWrapper,
    Inner,
    StyledWeekCalendar,
    WeekStatus,
    HeadingWrapper,
    CardLink,
    Heading,
    Description,
    StyledArrowLink
} from './Featured.styled';
import FlexBox from 'Elements/FlexBox';

export interface Props {
    heading: string;
    image: {
        src: StaticImageData | string;
        alt?: string;
    };
    calendarColour: string;
    weekStatus: { status: string; colour: string };
    week: number;
    description: string;
    link: {
        href: string;
        label: string;
    };
}

const Featured: React.FC<Props> = props => {
    const { heading, image, calendarColour, weekStatus, week, description, link } = props;
    const [isHovering, setIsHovering] = useState(false);

    const imageSpring = useSpring({
        transform: isHovering ? 'scale(1.03)' : 'scale(1)'
    });

    return (
        <Container>
            <Inner>
                <HeadingWrapper>
                    <StyledWeekCalendar week={week} color={calendarColour} />
                    <FlexBox direction="column" gap={{ small: '0.25rem', large: '0.75rem' }}>
                        <WeekStatus $weekStatusColour={weekStatus.colour}>
                            {weekStatus.status}
                        </WeekStatus>
                        <CardLink
                            href={link.href}
                            aria-label={link.label}
                            data-hover={isHovering}
                            onMouseOver={() => setIsHovering(true)}
                            onMouseLeave={() => setIsHovering(false)}
                            tabIndex={-1}
                        >
                            <Heading>{heading}</Heading>
                        </CardLink>
                    </FlexBox>
                </HeadingWrapper>
                <Description>{description}</Description>
                <StyledArrowLink
                    onFocus={() => setIsHovering(true)}
                    onBlur={() => setIsHovering(false)}
                    {...link}
                />
            </Inner>
            <ImageContainer>
                <ImageLink
                    href={link.href}
                    aria-label={link.label}
                    data-hover={isHovering}
                    onMouseOver={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    tabIndex={-1}
                >
                    <ImageWrapper style={imageSpring}>
                        <NextImage
                            src={image.src}
                            alt={image.alt || ''}
                            fill
                            style={{ objectFit: 'cover' }}
                        />
                    </ImageWrapper>
                </ImageLink>
            </ImageContainer>
        </Container>
    );
};

export default Featured;
